import { i18nObject } from 'src/boot/i18n'
import { defaultLang } from 'src/common/functions'
import { AVAILABLE_LANG_LIST } from 'src/common/config'
import { normalizeLocale } from 'src/common/utils/locale'
import { getConfig } from 'src/configs'

const scopeFiles = import.meta.glob('/src/i18n/scopes/**/*.json')
const loadedScopes = new Set()
const loadingScopes = new Map()
const { fallbackLangMap: fallbackMap } = getConfig()
const RETRY_STEP_MS = 500
const RETRY_JITTER_MS = 180

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

function getRetryDelayMs(attempt) {
  const baseDelay = RETRY_STEP_MS * attempt
  const jitter = Math.floor(Math.random() * RETRY_JITTER_MS)
  return baseDelay + jitter
}

function getScopeFilePath(scope, lang) {
  return `/src/i18n/scopes/${scope}/${lang}.json`
}

async function loadSingleScopeLanguage(scope, langIso) {
  const key = `${scope}:${langIso}`
  if (loadedScopes.has(key)) {
    return
  }

  if (loadingScopes.has(key)) {
    return loadingScopes.get(key)
  }

  const filePath = getScopeFilePath(scope, langIso)
  const loadFile = scopeFiles[filePath]

  if (!loadFile) {
    return
  }

  const loadPromise = loadFile()
    .then((module) => {
      i18nObject.global.mergeLocaleMessage(langIso, module.default)
      loadedScopes.add(key)
    })
    .finally(() => {
      loadingScopes.delete(key)
    })

  loadingScopes.set(key, loadPromise)
  return loadPromise
}

export async function loadI18nScope(scope, langIso, count = 5) {
  const normalizedLangIso = normalizeLocale(langIso, AVAILABLE_LANG_LIST) || defaultLang
  const isDefault = normalizedLangIso === defaultLang
  const fallbackLangArray = fallbackMap[normalizedLangIso] || fallbackMap.default || [defaultLang]
  const fallbackLangIso = normalizeLocale(fallbackLangArray[0], AVAILABLE_LANG_LIST) || defaultLang

  try {
    await loadSingleScopeLanguage(scope, normalizedLangIso)

    const fallbackLoaders = []
    if (!isDefault) {
      fallbackLoaders.push(loadSingleScopeLanguage(scope, fallbackLangIso))
      if (fallbackLangIso !== defaultLang) {
        fallbackLoaders.push(loadSingleScopeLanguage(scope, defaultLang))
      }
    }

    await Promise.all(fallbackLoaders)
  } catch (err) {
    const attemptsLeft = count - 1
    console.error(
      `[CAS] Failed to load i18n scope: ${scope}, lang: ${normalizedLangIso}, attempts left: ${attemptsLeft}`,
      err,
    )

    if (count > 1) {
      const attemptNumber = 6 - count
      const delayMs = getRetryDelayMs(attemptNumber)
      await sleep(delayMs)
      return loadI18nScope(scope, normalizedLangIso, count - 1)
    }

    if (normalizedLangIso !== defaultLang) {
      return loadI18nScope(scope, defaultLang, 5)
    }

    throw new Error(`Failed to load i18n scope "${scope}" for language "${normalizedLangIso}"`)
  }
}
